import { GrafanaTheme2 } from '@grafana/data';

/**
 * Default gradient colors for the assistant
 */
const GRADIENT_COLORS = {
  PURPLE: 'rgb(168, 85, 247)',
  ORANGE: 'rgb(249, 115, 22)',
} as const;

// FIXME: use SDK as single source of truth for these color schemes?

/**
 * Returns the colors of the background gradient based on the theme.
 *
 * @param theme - The Grafana theme object.
 * @returns The colors of the background gradient as tuple.
 */
export const getBackgroundGradientColors = (theme: GrafanaTheme2): [string, string] => {
  const defaultGradientColors: [string, string] = [GRADIENT_COLORS.PURPLE, GRADIENT_COLORS.ORANGE];

  // Check if the theme does not use gradients by default
  if (theme.name.toLowerCase() === 'dark' || theme.name.toLowerCase() === 'light') {
    return defaultGradientColors;
  }

  // If the theme uses gradients, extract the colors from the gradient string
  const gradientString = theme.colors.gradients.brandHorizontal;
  const hexColorRegex = /#[0-9A-Fa-f]{6}/g;
  const matches = gradientString.match(hexColorRegex);
  if (!matches || matches.length < 2) {
    // Fallback to default colors if extraction fails
    return defaultGradientColors;
  }

  return [matches[0], matches[1]];
};

/**
 * Creates animation keyframes for a rotating gradient border effect.
 * This is used for loading states in inputs and other components.
 *
 * @param theme - The Grafana theme object
 * @param backgroundColor - The background color to use in the animation
 * @param color1 - First gradient color (optional, defaults to theme purple)
 * @param color2 - Second gradient color (optional, defaults to theme orange)
 * @returns Animation keyframes object for use with @emotion/css keyframes
 *
 * @example
 * ```ts
 * const borderAnimation = keyframes({
 *   label: 'border-animation',
 *   ...createBorderAnimation(theme, theme.colors.background.primary),
 * });
 * ```
 */
export const createBorderAnimation = (
  theme: GrafanaTheme2,
  backgroundColor: string,
  color1?: string,
  color2?: string
): Record<string, { backgroundImage: string }> => {
  const animationSteps = Array.from({ length: 101 }, (_, i) => i);
  const animationFrames: Record<string, { backgroundImage: string }> = {};
  const [gradientColor1, gradientColor2] = getBackgroundGradientColors(theme);

  const finalColor1 = color1 ?? gradientColor1;
  const finalColor2 = color2 ?? gradientColor2;

  animationSteps.forEach((step) => {
    animationFrames[`${step}%`] = {
      backgroundImage: `
        linear-gradient(${backgroundColor}, ${backgroundColor}),
        conic-gradient(from ${step * 3.6}deg, transparent 60%, ${finalColor1} 80%, ${finalColor2} 100%, transparent 15%)
      `,
    };
  });

  return animationFrames;
};
